<template>
  <div class="smart-pagination">
    <!-- 总数和每页数量 -->
    <div class="pagination-info">
      <span class="total-count">共 {{ total }} 条</span>
      <el-select 
        v-model="localPageSize" 
        @change="handleSizeChange"
        size="small"
        style="width: 100px; margin-left: 12px;"
      >
        <el-option
          v-for="size in pageSizes"
          :key="size"
          :label="`${size}条/页`"
          :value="size"
        />
      </el-select>
    </div>

    <!-- 分页控制 -->
    <div class="pagination-controls">
      <!-- 上一页 -->
      <button 
        class="page-btn prev-btn"
        :disabled="currentPage <= 1"
        @click="handlePageChange(currentPage - 1)"
      >
        ‹
      </button>

      <!-- 页码 -->
      <div class="page-numbers">
        <!-- 第一页 -->
        <button 
          v-if="showFirstPage"
          class="page-btn"
          :class="{ active: currentPage === 1 }"
          @click="handlePageChange(1)"
        >
          1
        </button>

        <!-- 第一页省略号 -->
        <span v-if="showFirstEllipsis" class="ellipsis">...</span>

        <!-- 中间页码 -->
        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-btn"
          :class="{ active: currentPage === page }"
          @click="handlePageChange(page)"
        >
          {{ page }}
        </button>

        <!-- 最后一页省略号 -->
        <span v-if="showLastEllipsis" class="ellipsis">...</span>

        <!-- 最后一页 -->
        <button 
          v-if="showLastPage"
          class="page-btn"
          :class="{ active: currentPage === totalPages }"
          @click="handlePageChange(totalPages)"
        >
          {{ totalPages }}
        </button>
      </div>

      <!-- 下一页 -->
      <button 
        class="page-btn next-btn"
        :disabled="currentPage >= totalPages"
        @click="handlePageChange(currentPage + 1)"
      >
        ›
      </button>
    </div>


  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'SmartPagination',
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    pageSize: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    pageSizes: {
      type: Array,
      default: () => [20, 50, 100, 200, 500]
    }
  },
  emits: ['update:current-page', 'update:page-size', 'current-page-change', 'page-size-change'],
  setup(props, { emit }) {
    const localPageSize = ref(props.pageSize)

    // 计算总页数
    const totalPages = computed(() => Math.ceil(props.total / props.pageSize))

    // 计算可见页码
    const visiblePages = computed(() => {
      const pages = []
      const maxVisible = 3 // 最多显示5个页码
      
      if (totalPages.value <= maxVisible) {
        // 如果总页数小于等于最大显示数，显示所有页码
        for (let i = 1; i <= totalPages.value; i++) {
          pages.push(i)
        }
      } else {
        // 动态计算显示的页码范围
        let start = Math.max(1, props.currentPage - Math.floor(maxVisible / 2))
        let end = Math.min(totalPages.value, start + maxVisible - 1)
        
        // 调整起始位置，确保显示足够的页码
        if (end - start + 1 < maxVisible) {
          start = Math.max(1, end - maxVisible + 1)
        }
        
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }
      }
      
      return pages
    })

    // 是否显示第一页
    const showFirstPage = computed(() => {
      return visiblePages.value[0] > 1
    })

    // 是否显示第一页省略号
    const showFirstEllipsis = computed(() => {
      return visiblePages.value[0] > 2
    })

    // 是否显示最后一页
    const showLastPage = computed(() => {
      return visiblePages.value[visiblePages.value.length - 1] < totalPages.value
    })

    // 是否显示最后一页省略号
    const showLastEllipsis = computed(() => {
      return visiblePages.value[visiblePages.value.length - 1] < totalPages.value - 1
    })

    // 处理页码变化
    const handlePageChange = (page) => {
      console.log('SmartPagination 页码变化:', page)
      if (page >= 1 && page <= totalPages.value) {
        console.log('发出事件: update:current-page', page)
        emit('update:current-page', page)
        // 同时发出自定义事件
        emit('current-page-change', page)
      }
    }

    // 处理每页数量变化
    const handleSizeChange = (size) => {
      emit('update:page-size', size)
      // 重置到第一页
      emit('update:current-page', 1)
      // 同时发出自定义事件
      emit('page-size-change', size)
    }





    return {
      localPageSize,
      totalPages,
      visiblePages,
      showFirstPage,
      showFirstEllipsis,
      showLastPage,
      showLastEllipsis,
      handlePageChange,
      handleSizeChange
    }
  }
}
</script>

<style scoped>
.smart-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  font-size: 12px;
}

.pagination-info {
  display: flex;
  align-items: center;
}

.total-count {
  color: #666;
  font-size: 12px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  min-width: 28px;
  height: 28px;
  border: 1px solid #d9d9d9;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #666;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #409eff;
  color: #409eff;
}

.page-btn.active {
  background: #409eff;
  border-color: #409eff;
  color: white;
}

.page-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.prev-btn,
.next-btn {
  font-size: 16px;
  font-weight: bold;
}

.ellipsis {
  color: #999;
  padding: 0 8px;
  font-size: 12px;
}
</style>
